// Conflicts are per (bay, shelf) cell; only rules about the banded variant can clash with a fence.
import type {
  SaveVariantRestrictionGroup,
  SaveVariantRestrictionRule,
} from "@CommonApi/merchflow/save-variant-restrictions";
import type { RestrictionPlacement } from "./studioTypes";

export interface RestrictionFixture {
  bays: number;
  shelves: number;
}

export type ConflictKind = "allow-list-capture" | "contradiction" | "mirror-exclusion";

export type ConflictResolution = "narrow" | "split" | "delete";

export interface RuleConflict {
  kind: ConflictKind;
  groupId: string;
  rule: SaveVariantRestrictionRule;
  cells: readonly string[];
  reason: string;
  resolution: ConflictResolution;
  replacement: readonly SaveVariantRestrictionRule[];
}

export interface ConflictScan {
  conflicts: readonly RuleConflict[];
  adjustedGroups: readonly SaveVariantRestrictionGroup[];
  deleted: readonly { groupId: string; rule: SaveVariantRestrictionRule }[];
}

const cellKey = (bay: number, shelf: number) => `${bay}:${shelf}`;

export const expandBays = (bayRule: string, fixture: RestrictionFixture): number[] => {
  const normalized = bayRule.trim().toLowerCase();
  if (normalized === "*" || normalized === "all") {
    return Array.from({ length: fixture.bays }, (_unused, index) => index + 1);
  }
  return bayRule
    .split("|")
    .map((part) => Number(part.trim()))
    .filter((bay) => Number.isFinite(bay) && bay >= 1 && bay <= fixture.bays);
};

/** Unparseable shelf rules yield no shelves, not all — safer than a silent wildcard. */
export const expandShelves = (shelfRule: string, fixture: RestrictionFixture): number[] => {
  const raw = shelfRule.trim();
  const normalized = raw.toLowerCase();
  if (normalized === "*" || normalized === "all") {
    return Array.from({ length: fixture.shelves }, (_unused, index) => index + 1);
  }
  const literal = Number(raw);
  if (Number.isFinite(literal) && literal >= 1 && literal <= fixture.shelves) return [literal];
  if (normalized.startsWith("top")) {
    if (normalized === "top") return [1];
    const plus = Number(raw.split("+")[1]?.trim());
    return Number.isFinite(plus) ? [1 + plus] : [];
  }
  if (normalized.startsWith("bottom")) {
    if (normalized === "bottom") return [fixture.shelves];
    const minus = Number(raw.split("-")[1]?.trim());
    return Number.isFinite(minus) ? [fixture.shelves - minus] : [];
  }
  return [];
};

export const rulesCells = (
  rule: SaveVariantRestrictionRule,
  fixture: RestrictionFixture,
): { bays: number[]; shelves: number[]; keys: Set<string> } => {
  const bays = expandBays(rule.bay_number_rule, fixture);
  const shelves = expandShelves(rule.shelf_rule, fixture);
  const keys = new Set<string>();
  for (const bay of bays) for (const shelf of shelves) keys.add(cellKey(bay, shelf));
  return { bays, shelves, keys };
};

const placementCells = (placement: RestrictionPlacement) => {
  const keys = new Set<string>();
  const baysByShelf = new Map<number, Set<number>>();
  for (const cell of placement.cells) {
    baysByShelf.set(cell.shelf, new Set(cell.bays));
    for (const bay of cell.bays) keys.add(cellKey(bay, cell.shelf));
  }
  return { keys, baysByShelf };
};

/** `fence` is exclusion cells; `keptCells` is the band's seats (mirror check only). */
export const detectRuleConflicts = (
  existingGroups: readonly SaveVariantRestrictionGroup[],
  fence: RestrictionPlacement,
  bandVariant: string,
  shippedVariants: readonly string[],
  fixture: RestrictionFixture,
  keptCells?: RestrictionPlacement,
): ConflictScan => {
  const mine = placementCells(fence);
  const kept = keptCells ? placementCells(keptCells) : null;
  const shipped = new Set(shippedVariants);
  const conflicts: RuleConflict[] = [];
  const deleted: { groupId: string; rule: SaveVariantRestrictionRule }[] = [];

  const adjustedGroups = existingGroups.map((group) => {
    const rules: SaveVariantRestrictionRule[] = [];
    for (const rule of group.rules) {
      if (!shipped.has(rule.variant)) {
        rules.push(rule);
        continue;
      }
      const resolved = rulesCells(rule, fixture);
      const fenceOverlap = [...resolved.keys].filter((key) => mine.keys.has(key));
      const keptOverlap = kept ? [...resolved.keys].filter((key) => kept.keys.has(key)) : [];
      if (fenceOverlap.length === 0 && keptOverlap.length === 0) {
        rules.push(rule);
        continue;
      }

      const isBand = rule.variant === bandVariant;
      const setsAllowList = rule.condition === "CAN_HAVE" || rule.condition === "MUST_HAVE_ONLY";
      const requires = rule.condition === "MUST_HAVE" || rule.condition === "MUST_HAVE_ONLY";
      const forbids = rule.condition === "MUST_NOT_HAVE";

      let kind: ConflictKind | null = null;
      let reason = "";
      let against = mine;
      let overlap = fenceOverlap;

      if (isBand && requires && fenceOverlap.length > 0) {
        kind = "contradiction";
        reason =
          `"${bandVariant}" is required on these cells by a stored ${rule.condition}, and the ` +
          "fence forbids it there. must_be_on and must_not_be_on cannot both hold.";
      } else if (isBand && setsAllowList && fenceOverlap.length > 0) {
        kind = "allow-list-capture";
        reason =
          `"${bandVariant}" holds a stored ${rule.condition} on these cells, which sets may_have ` +
          "and turns permits_all off for them. translate_shelves_may_have then strips the band's " +
          "own may_have because the fence's must_not_have wins, so the cells end up an allow-list " +
          "the band is not on — and every sibling the stored rule does not name loses cells " +
          "nothing intended to restrict.";
      } else if (isBand && forbids && keptOverlap.length > 0) {
        kind = "mirror-exclusion";
        against = kept ?? mine;
        overlap = keptOverlap;
        reason =
          `"${bandVariant}" is forbidden on these cells by a stored MUST_NOT_HAVE, and these are ` +
          "the shelves the plan keeps it on.";
      }

      if (!kind) {
        rules.push(rule);
        continue;
      }

      const touchedShelves = resolved.shelves.filter(
        (shelf) =>
          against.baysByShelf.has(shelf) &&
          resolved.bays.some((bay) => against.baysByShelf.get(shelf)?.has(bay)),
      );
      const replacement: SaveVariantRestrictionRule[] = [];
      let resolution: ConflictResolution;

      if (resolved.shelves.length === 1) {
        const keptBays = resolved.bays.filter(
          (bay) => !against.baysByShelf.get(resolved.shelves[0])?.has(bay),
        );
        if (keptBays.length > 0) {
          resolution = "narrow";
          replacement.push({ ...rule, bay_number_rule: keptBays.join(" | ") });
        } else {
          resolution = "delete";
        }
      } else {
        // Split so untouched shelves keep their full bay list.
        for (const shelf of resolved.shelves) {
          const claimed = against.baysByShelf.get(shelf);
          const keptBays = claimed
            ? resolved.bays.filter((bay) => !claimed.has(bay))
            : [...resolved.bays];
          if (keptBays.length === 0) continue;
          replacement.push({
            ...rule,
            bay_number_rule: keptBays.join(" | "),
            shelf_rule: String(shelf),
          });
        }
        resolution = replacement.length === 0 ? "delete" : "split";
      }

      conflicts.push({
        kind,
        groupId: group.variant_group_id,
        rule,
        cells: overlap.sort(),
        reason:
          reason +
          (resolution === "delete"
            ? " Nothing of it survives, so it is deleted outright."
            : resolution === "narrow"
              ? ` Narrowed to the bays it keeps; shelf ${resolved.shelves[0]} unchanged elsewhere.`
              : ` Split per shelf so the ${resolved.shelves.length - touchedShelves.length} shelf(s) this placement never touches keep their full bay list.`),
        resolution,
        replacement,
      });
      if (resolution === "delete") deleted.push({ groupId: group.variant_group_id, rule });
      rules.push(...replacement);
    }
    return { ...group, rules };
  });

  return { conflicts, adjustedGroups, deleted };
};
